import { useState } from 'react';
import {
  amountPayload,
  buildAmountRows,
  type EditableAmountRow,
  type NoteDialogData,
  type NoteRow,
} from '@/core/models/notes.models';
import type { DialogComponentProps } from '@/kit/dialog';
import { InputField, TextareaField } from '@/kit/FormField';
import { RequiredMark } from '@/kit/RequiredMark';
import { Select } from '@/kit/Select';
import { AmountRowsTable } from './AmountRowsTable';
import { FieldMessage, NoteDialogShell, useNoteSave } from './NoteDialogParts';
import { withPrior } from './NamedAmountDialog';

/** Add / Edit one related party. */
export function RelatedPartyDialog({ data, dialogRef }: DialogComponentProps<NoteDialogData, NoteRow>) {
  const content = data?.note?.content ?? {};
  const [model, setModel] = useState({
    name: (content.name ?? '') as string,
    type: (content.type ?? null) as string | null,
    relationship: (content.relationship ?? '') as string,
    description: (content.description ?? '') as string,
  });
  const [rows, setRows] = useState<EditableAmountRow[]>(() =>
    withPrior(buildAmountRows('relatedParty', content.rows), data, content.name ?? ''),
  );
  const { isSaving, save, submitted } = useNoteSave(data, dialogRef);
  const nameMissing = model.name.trim().length === 0;
  const relationshipMissing = model.relationship.trim().length === 0;
  const isEdit = !!data?.note;

  return (
    <NoteDialogShell
      title={`${isEdit ? 'Edit' : 'Add'} Related Party`}
      isSaving={isSaving}
      onSave={() => save(() => ({ ...model, rows: amountPayload(rows) }), !nameMissing && !relationshipMissing)}
    >
      <div>
        <div className="d-flex gap-20">
          <div className="form-field">
            <p className="fw-bold">
              Name <RequiredMark />
            </p>
            <InputField
              fieldClassName="w-200"
              autoComplete="off"
              charMode="printable"
              maxLength={255}
              invalid={submitted && nameMissing}
              value={model.name}
              onChange={(event) => setModel((current) => ({ ...current, name: event.target.value }))}
              onBlur={() => setRows((current) => withPrior(current, data, model.name))}
            />
            <FieldMessage show={submitted && nameMissing}>Enter the related party's name.</FieldMessage>
          </div>
          <div className="form-field">
            <p className="fw-bold">Type</p>
            <Select
              className="w-200"
              panelClass="myPanelClass"
              items={data?.relatedPartyTypes ?? []}
              bindLabel="label"
              bindValue="value"
              value={model.type}
              onChange={(value) => setModel((current) => ({ ...current, type: value as string | null }))}
            />
          </div>
        </div>
        <div className="form-field">
          <p className="fw-bold">
            Relationship <RequiredMark />
          </p>
          <InputField
            fieldClassName="wp-100"
            autoComplete="off"
            maxLength={255}
            invalid={submitted && relationshipMissing}
            value={model.relationship}
            onChange={(event) => setModel((current) => ({ ...current, relationship: event.target.value }))}
          />
          <FieldMessage show={submitted && relationshipMissing}>Enter the relationship.</FieldMessage>
        </div>
        <div className="form-field textarea">
          <p className="fw-bold">Description of the transaction and general amounts involved</p>
          <TextareaField
            fieldClassName="wp-100"
            autoComplete="off"
            maxLength={4000}
            value={model.description}
            onChange={(event) => setModel((current) => ({ ...current, description: event.target.value }))}
          />
        </div>

        <AmountRowsTable
          rows={rows}
          onCellChange={(index, column, value) =>
            setRows((current) => current.map((row, i) => (i === index ? { ...row, [column]: value } : row)))
          }
        />
      </div>
    </NoteDialogShell>
  );
}
